import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, FileText, Calendar, User, Mail, Phone, Check, X, Clock, AlertTriangle, Send, Upload, Paperclip } from 'lucide-react';
import adminService from '../../services/adminService';

const InvoiceRequestManager = () => {
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updateStatus, setUpdateStatus] = useState({
    loading: false,
    error: null,
    success: false
  });

  // États pour l'envoi d'email avec PDF
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: ''
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [viewMode, setViewModeState] = useState(() => {
    try { return localStorage.getItem("adminViewMode") || "cards"; } catch { return "cards"; }
  });
  const setViewMode = (v) => { setViewModeState(v); try { localStorage.setItem("adminViewMode", v); } catch (_) {} };

  const fetchInvoiceRequests = async () => {
    setLoading(true);
    try {
      const response = await adminService.getInvoiceRequests();
      setInvoiceRequests(response.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors du chargement des demandes');
      console.error('Erreur lors du chargement des demandes de facture:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceRequests();
  }, []);

  const handleStatusChange = async (requestId, newStatus, notes = '') => {
    setUpdateStatus({ loading: true, error: null, success: false });
    try {
      await adminService.updateInvoiceRequestStatus(requestId, newStatus, notes);
      // Mise à jour locale de l'état
      setInvoiceRequests(prevRequests =>
        prevRequests.map(req =>
          req._id === requestId
            ? { ...req, status: newStatus, notes: notes || req.notes, processedAt: newStatus === 'pending' ? null : new Date().toISOString() }
            : req
        )
      );

      // Si on met à jour la demande actuellement sélectionnée
      if (selectedRequest && selectedRequest._id === requestId) {
        setSelectedRequest(prev => ({
          ...prev,
          status: newStatus,
          notes: notes || prev.notes,
          processedAt: newStatus === 'pending' ? null : new Date().toISOString()
        }));
      }

      setUpdateStatus({ loading: false, error: null, success: true });

      // Reset du statut de succès après 3 secondes
      setTimeout(() => {
        setUpdateStatus(prev => ({ ...prev, success: false }));
      }, 3000);
    } catch (err) {
      setUpdateStatus({ loading: false, error: err.message || 'Erreur lors de la mise à jour', success: false });
    }
  };

  const getFilteredRequests = () => {
    return invoiceRequests.filter(request => {
      // Filtre par statut
      if (statusFilter !== 'all' && request.status !== statusFilter) {
        return false;
      }

      // Filtre par recherche
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          request.contactInfo?.name?.toLowerCase().includes(query) ||
          request.contactInfo?.email?.toLowerCase().includes(query) ||
          request.reservationId?.toLowerCase().includes(query) ||
          request.reservationType?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'processed':
        return <RefreshCw className="h-4 w-4" />;
      case 'completed':
        return <Check className="h-4 w-4" />;
      case 'rejected':
        return <X className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReservationTypeLabel = (type) => {
    const types = {
      hotel: 'Hôtel',
      circuit: 'Circuit',
      transport: 'Transport',
      visite: 'Visite guidée',
      voiture: 'Location de voiture'
    };
    return types[type] || type;
  };

  const filteredRequests = getFilteredRequests();

  const statusLabel = (s) => ({ pending: 'En attente', processed: 'En traitement', completed: 'Complété', rejected: 'Refusé' }[s] || s);

  const VIEW_OPTIONS = [
    { key: 'cards', label: 'Cartes', icon: '▦' },
    { key: 'table', label: 'Tableau', icon: '☰' },
    { key: 'compact', label: 'Compact', icon: '≣' },
  ];
  const ViewSwitcher = () => (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1">
      {VIEW_OPTIONS.map((o) => (
        <button
          key={o.key}
          onClick={() => setViewMode(o.key)}
          title={o.label}
          className={`px-2.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${viewMode === o.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span className="mr-1">{o.icon}</span><span className="hidden sm:inline">{o.label}</span>
        </button>
      ))}
    </div>
  );
  const StatusPill = ({ status }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusColor(status)}`}>
      {getStatusIcon(status)}<span className="ml-1">{statusLabel(status)}</span>
    </span>
  );

  // Fonctions pour la gestion d'email avec PDF
  const handleOpenEmailModal = (request) => {
    setSelectedRequest(request);
    setEmailData({
      subject: `Votre facture pour la réservation ${request.reservationId}`,
      message: `Bonjour ${request.contactInfo?.name || 'Cher client'},\n\nNous vous remercions pour votre demande de facture.\n\nVeuillez trouver en pièce jointe votre facture pour la réservation ${request.reservationId} (${getReservationTypeLabel(request.reservationType)}).\n\nSi vous avez des questions concernant cette facture, n'hésitez pas à nous contacter.\n\nCordialement,\nL'équipe Le Palais Touristique`
    });
    setShowEmailModal(true);
  };

  const handlePdfUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      // Vérifier la taille du fichier (limite à 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB en bytes
      if (file.size > maxSize) {
        alert('Le fichier PDF est trop volumineux. Veuillez sélectionner un fichier de moins de 10MB.');
        event.target.value = '';
        return;
      }
      setPdfFile(file);
    } else {
      alert('Veuillez sélectionner un fichier PDF valide');
      event.target.value = '';
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Supprimer le préfixe "data:application/pdf;base64," pour obtenir seulement le base64
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleSendEmail = async () => {
    if (!selectedRequest || !emailData.subject || !emailData.message) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSendingEmail(true);
    try {
      let pdfBuffer = null;
      let pdfFilename = null;

      if (pdfFile) {
        pdfBuffer = await convertFileToBase64(pdfFile);
        pdfFilename = pdfFile.name;
      }

      await adminService.sendInvoiceEmailWithPDF(
        selectedRequest._id,
        emailData.subject,
        emailData.message,
        pdfBuffer,
        pdfFilename
      );

      // Mettre à jour le statut local de la demande
      setInvoiceRequests(prevRequests =>
        prevRequests.map(req =>
          req._id === selectedRequest._id
            ? { ...req, status: 'completed', processedAt: new Date().toISOString() }
            : req
        )
      );

      // Mettre à jour la demande sélectionnée
      setSelectedRequest(prev => ({
        ...prev,
        status: 'completed',
        processedAt: new Date().toISOString()
      }));

      alert('Email envoyé avec succès !');
      setShowEmailModal(false);
      setEmailData({ subject: '', message: '' });
      setPdfFile(null);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      alert('Erreur lors de l\'envoi de l\'email: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-amber-500 to-orange-600">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <FileText className="h-6 w-6" />
          Gestion des demandes de facture
        </h2>
        <p className="text-white/80 mt-1">
          Consultez et traitez les demandes de facture des clients
        </p>
      </div>

      {/* Filtres et recherche */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher par nom, email, ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="processed">En traitement</option>
              <option value="completed">Complété</option>
              <option value="rejected">Refusé</option>
            </select>
          </div>

          <button
            onClick={fetchInvoiceRequests}
            className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>

          <ViewSwitcher />
        </div>
      </div>

      <div className="flex flex-col md:flex-row h-[calc(100vh-350px)]">
        {/* Liste des demandes */}
        <div className="w-full md:w-2/5 lg:w-1/3 overflow-y-auto border-r border-slate-200">
          {loading ? (
            <div className="p-3 space-y-3">
              <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl border border-slate-100 p-4 space-y-2.5">
                  <div className="h-4 w-2/3 rounded nz-skel" />
                  <div className="h-3 w-1/2 rounded nz-skel" />
                  <div className="h-3 w-1/3 rounded nz-skel" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              {error}
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-2 text-slate-400" />
              <p>Aucune demande trouvée</p>
            </div>
          ) : viewMode === 'table' ? (
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80 sticky top-0">
                <tr>
                  {['Client', 'Type', 'Statut'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-bold text-slate-400 uppercase tracking-[0.08em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRequests.map((request) => (
                  <tr
                    key={request._id}
                    onClick={() => setSelectedRequest(request)}
                    className={`cursor-pointer transition-colors ${selectedRequest?._id === request._id ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800 text-sm truncate">{request.contactInfo?.name || 'Client inconnu'}</div>
                      <div className="text-xs text-slate-400 truncate">{request.contactInfo?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{getReservationTypeLabel(request.reservationType)}</td>
                    <td className="px-4 py-3"><StatusPill status={request.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : viewMode === 'compact' ? (
            <div className="divide-y divide-slate-100">
              {filteredRequests.map((request) => (
                <button
                  key={request._id}
                  onClick={() => setSelectedRequest(request)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors ${selectedRequest?._id === request._id ? 'bg-orange-50 border-l-4 border-orange-500' : 'hover:bg-slate-50'}`}
                >
                  <div className="w-9 h-9 flex-none rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center font-bold text-sm">{(request.contactInfo?.name || '?').charAt(0).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm truncate">{request.contactInfo?.name || 'Client inconnu'}</p>
                    <p className="text-xs text-slate-400 truncate">{getReservationTypeLabel(request.reservationType)} · {request.contactInfo?.email}</p>
                  </div>
                  <StatusPill status={request.status} />
                </button>
              ))}
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {filteredRequests.map((request) => (
                <button
                  key={request._id}
                  onClick={() => setSelectedRequest(request)}
                  className={`w-full text-left rounded-2xl border p-4 transition-all hover:shadow-md ${selectedRequest?._id === request._id ? 'border-orange-300 bg-orange-50/60 shadow-sm' : 'border-slate-100 bg-white hover:-translate-y-0.5'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-none rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center font-black">{(request.contactInfo?.name || '?').charAt(0).toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold text-slate-800 truncate">{request.contactInfo?.name || 'Client inconnu'}</div>
                        <StatusPill status={request.status} />
                      </div>
                      <div className="text-sm text-slate-500 truncate">{request.contactInfo?.email}</div>
                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{getReservationTypeLabel(request.reservationType)}</span>
                        <span>{formatDate(request.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Détails de la demande */}
        <div className="w-full md:w-3/5 lg:w-2/3 overflow-y-auto p-6">
          {selectedRequest ? (
            <div>
              <div className="border-b border-slate-200 pb-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Détails de la demande</h3>

                {/* Informations de contact */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-slate-700 mb-3">Informations de contact</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-slate-400 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500">Nom</div>
                        <div className="font-medium">{selectedRequest.contactInfo?.name || 'Non spécifié'}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-slate-400 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500">Email</div>
                        <div className="font-medium">{selectedRequest.contactInfo?.email || 'Non spécifié'}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-slate-400 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500">Téléphone</div>
                        <div className="font-medium">{selectedRequest.contactInfo?.phone || 'Non spécifié'}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-slate-400 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500">Date de la demande</div>
                        <div className="font-medium">{formatDate(selectedRequest.createdAt)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations de réservation */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-slate-700 mb-3">Informations de réservation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-slate-500">Type de réservation</div>
                      <div className="font-medium">{getReservationTypeLabel(selectedRequest.reservationType)}</div>
                    </div>

                    <div>
                      <div className="text-sm text-slate-500">ID de réservation</div>
                      <div className="font-medium font-mono">{selectedRequest.reservationId}</div>
                    </div>
                  </div>
                </div>

                {/* Message du client */}
                {selectedRequest.contactInfo?.message && (
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-slate-700 mb-2">Message du client</h4>
                    <p className="text-slate-700">
                      {selectedRequest.contactInfo.message}
                    </p>
                  </div>
                )}

                {/* Notes administratives */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-slate-700 mb-2">Notes administratives</h4>
                  <textarea
                    className="w-full p-2 border border-slate-300 rounded-md"
                    rows="3"
                    placeholder="Ajouter des notes (visibles uniquement par les administrateurs)"
                    value={selectedRequest.notes || ''}
                    onChange={(e) => {
                      setSelectedRequest({
                        ...selectedRequest,
                        notes: e.target.value
                      });
                    }}
                  ></textarea>
                </div>

                {/* Statut actuel */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-700 mb-2">Statut actuel</h4>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedRequest.status)}`}>
                    {getStatusIcon(selectedRequest.status)}
                    <span className="ml-1">
                      {selectedRequest.status === 'pending' && 'En attente'}
                      {selectedRequest.status === 'processed' && 'En traitement'}
                      {selectedRequest.status === 'completed' && 'Complété'}
                      {selectedRequest.status === 'rejected' && 'Refusé'}
                    </span>
                  </div>

                  {selectedRequest.processedAt && (
                    <div className="text-sm text-slate-500 mt-2">
                      Traité le: {formatDate(selectedRequest.processedAt)}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="pt-6">
                <h3 className="text-lg font-medium text-slate-900 mb-3">Changer le statut</h3>

                {updateStatus.error && (
                  <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    {updateStatus.error}
                  </div>
                )}

                {updateStatus.success && (
                  <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md flex items-center gap-2">
                    <Check className="h-5 w-5" />
                    Statut mis à jour avec succès
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleStatusChange(selectedRequest._id, 'pending', selectedRequest.notes)}
                    disabled={updateStatus.loading || selectedRequest.status === 'pending'}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 ${selectedRequest.status === 'pending'
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      }`}
                  >
                    <Clock className="h-4 w-4" />
                    En attente
                  </button>

                  <button
                    onClick={() => handleStatusChange(selectedRequest._id, 'processed', selectedRequest.notes)}
                    disabled={updateStatus.loading || selectedRequest.status === 'processed'}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 ${selectedRequest.status === 'processed'
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}
                  >
                    <RefreshCw className="h-4 w-4" />
                    En traitement
                  </button>

                  <button
                    onClick={() => handleStatusChange(selectedRequest._id, 'completed', selectedRequest.notes)}
                    disabled={updateStatus.loading || selectedRequest.status === 'completed'}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 ${selectedRequest.status === 'completed'
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                  >
                    <Check className="h-4 w-4" />
                    Complété
                  </button>

                  <button
                    onClick={() => handleStatusChange(selectedRequest._id, 'rejected', selectedRequest.notes)}
                    disabled={updateStatus.loading || selectedRequest.status === 'rejected'}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 ${selectedRequest.status === 'rejected'
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                  >
                    <X className="h-4 w-4" />
                    Refuser
                  </button>
                </div>

                {/* Nouvelle section pour envoyer un email avec PDF */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <h3 className="text-lg font-medium text-slate-900 mb-3">Envoyer la facture par email</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Répondez à la demande du client en envoyant un email avec la facture en pièce jointe PDF.
                  </p>
                  <button
                    onClick={() => handleOpenEmailModal(selectedRequest)}
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all flex items-center gap-2 font-medium"
                  >
                    <Send className="h-5 w-5" />
                    Envoyer un email avec facture
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <FileText className="h-16 w-16 mb-4" />
              <p className="text-lg">Sélectionnez une demande pour voir ses détails</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour envoyer un email avec PDF */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-amber-500 to-orange-600">
              <h3 className="text-xl font-bold text-white flex items-center gap-3">
                <Send className="h-6 w-6" />
                Envoyer la facture par email
              </h3>
              <p className="text-white/80 mt-1">
                Répondez à la demande de {selectedRequest?.contactInfo?.name}
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations du destinataire */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-2">Destinataire</h4>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <div>
                    <div className="font-medium">{selectedRequest?.contactInfo?.name}</div>
                    <div className="text-sm text-slate-500">{selectedRequest?.contactInfo?.email}</div>
                  </div>
                </div>
              </div>

              {/* Sujet de l'email */}
              <div>
                <label className="block font-medium text-slate-700 mb-2">
                  Sujet de l'email *
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))
                  }
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Objet de l'email"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block font-medium text-slate-700 mb-2">
                  Message *
                </label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))
                  }
                  rows={8}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  placeholder="Rédigez votre message..."
                />
              </div>

              {/* Upload du PDF */}
              <div>
                <label className="block font-medium text-slate-700 mb-2">
                  Facture PDF (optionnel)
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      {pdfFile ? (
                        <div className="flex items-center gap-3 text-green-600">
                          <Paperclip className="h-8 w-8" />
                          <div>
                            <div className="font-medium">{pdfFile.name}</div>
                            <div className="text-sm text-slate-500">
                              {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-12 w-12 text-slate-400 mb-2" />
                          <p className="text-slate-600 font-medium">
                            Cliquez pour sélectionner un fichier PDF
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            Fichiers PDF uniquement (max 10MB)
                          </p>
                        </>
                      )}
                    </div>
                  </label>
                </div>
                {pdfFile && (
                  <button
                    onClick={() => setPdfFile(null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Supprimer le fichier
                  </button>
                )}
              </div>

              {/* Aperçu des informations */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Informations de la réservation</h4>
                <div className="text-sm text-blue-800">
                  <p><strong>Type :</strong> {getReservationTypeLabel(selectedRequest?.reservationType)}</p>
                  <p><strong>ID Réservation :</strong> {selectedRequest?.reservationId}</p>
                </div>
              </div>
            </div>

            {/* Actions du modal */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailData({ subject: '', message: '' });
                  setPdfFile(null);
                }}
                disabled={sendingEmail}
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailData.subject || !emailData.message}
                className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {sendingEmail ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Envoyer l'email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceRequestManager;